// Profile-completion gate for CSOC students inside School of Chess.
//
// CSOC students reach the class/meeting flow through this screen, and a
// half-finished profile breaks things downstream (no username on the
// leaderboard, no linked accounts to pull games from). So unlike the
// once-ever welcome nudge in Application.tsx, this one is persistent AND
// non-dismissable: it stays up on every tab until the profile is actually
// complete.
//
// It returns a boolean rather than opening the global onboarding modal. That
// modal covers the whole viewport, left menu included, so a non-dismissable
// version of it would strand the user. The caller renders CsocProfileGate,
// which only covers the screen's own content — see that file.
//
// Scope is intentionally narrow: CSOC subscribers only. Everyone else keeps the
// quiet surfaces (header ring, avatar dropdown, Settings card).

import {
  useOnboardingProgress,
  useOnboardingProgressStore,
} from '@/Store/onboardingProgressStore';
import {StepKey} from '@/Utils/onboardingSteps';

/**
 * What this gate requires — user details and username.
 *
 * Deliberately excludes `connect_accounts`: a CSOC student may have no
 * Lichess/Chess.com account at all, and since this gate can't be dismissed,
 * requiring one would lock them out of School of Chess with no way to comply.
 * That step still counts towards the global onboarding percent everywhere else.
 */
export const CSOC_GATE_STEPS: StepKey[] = ['profile', 'username'];

// TEMPORARILY DISABLED (2026-08-18): real students were getting blocked
// while the player_linked signal is still being field-validated. Flip this
// back to `true` to re-enable — none of the computation below changed.
const CSOC_PROFILE_GATE_ENABLED = false;

interface Args {
  /** True when the CSOC subscription list came back non-empty. */
  hasCsocSubscription: boolean;
}

export default function useCsocProfileNudge({hasCsocSubscription}: Args) {
  const hydrated = useOnboardingProgressStore(s => s.hydrated);
  // hydrated only means local storage was read. Until the dashboard summary has
  // also landed, every step reads as not-done — gating then would block CSOC
  // users whose profile is actually complete.
  const summaryLoaded = useOnboardingProgressStore(
    s => !!s.summarySnapshot?.user,
  );
  const summaryUser = useOnboardingProgressStore(s => s.summarySnapshot?.user);
  const {steps} = useOnboardingProgress();

  // NOT useOnboardingComplete(): that's the 3-step number, which would keep the
  // gate up for anyone without a linked chess account.
  //
  // The shared 'profile' step also requires a pincode (for batch assignment),
  // but that's not needed to unblock School of Chess, so this gate checks
  // first_name directly instead of deferring to that step's `done` flag.
  // pincode still counts towards the global onboarding percent everywhere else.
  const usernameStep = steps.find(s => s.key === 'username');
  const profileStep = steps.find(s => s.key === 'profile');
  const complete = !!profileStep?.done && !!usernameStep?.done;

  const shouldGate =
    hydrated && summaryLoaded && hasCsocSubscription && !complete;

  return CSOC_PROFILE_GATE_ENABLED && shouldGate;
}
